import { isLightTheme, resolveSemanticColor } from '@/theme/semanticTokens';
import {
  chartStatusDarkenCoefficient,
  lightModeChartStatusColors,
  uiTokens,
} from '@/theme/uiTokens';
import { darken } from '@/ui/primitives/colorUtils';
import type { MetricCardItem } from '@/features/metrics/components/statusMetricCards';
import type { Theme } from '@/ui/primitives';

const darkModeStatusMetricAccentColors = {
  green: '#68c85f',
  red: '#e0665d',
  orange: '#e0a02f',
  brown: '#a88678',
  teal: '#009f9f',
  blue: '#5fb0e0',
  purple: '#9365d8',
  pink: '#d31566',
  neutral: '#c0c6cf',
} as const;

const lightModeStatusMetricAccentColors = lightModeChartStatusColors;

export const resolveStatusMetricAccentColor = (
  theme: Theme,
  statusTone: MetricCardItem['statusTone'],
  fallbackColor: string,
): string => {
  if (statusTone === 'primary') {
    return isLightTheme(theme)
      ? resolveSemanticColor(theme, 'accent')
      : darkModeStatusMetricAccentColors.teal;
  }
  if (isLightTheme(theme)) {
    if (statusTone === 'success') return lightModeStatusMetricAccentColors.green;
    if (statusTone === 'error') return lightModeStatusMetricAccentColors.red;
    if (statusTone === 'warning') return lightModeStatusMetricAccentColors.orange;
    if (statusTone === 'brown') return lightModeStatusMetricAccentColors.brown;
    if (statusTone === 'info') return lightModeStatusMetricAccentColors.blue;
    return lightModeStatusMetricAccentColors.neutral;
  }
  if (statusTone === 'success') return darkModeStatusMetricAccentColors.green;
  if (statusTone === 'error') return darkModeStatusMetricAccentColors.red;
  if (statusTone === 'warning') return darkModeStatusMetricAccentColors.orange;
  if (statusTone === 'brown') return darkModeStatusMetricAccentColors.brown;
  if (statusTone === 'info') return darkModeStatusMetricAccentColors.blue;
  return fallbackColor || darkModeStatusMetricAccentColors.neutral;
};

export const resolveMiniMetricAccentColors = (
  theme: Theme,
): {
  passRate: string;
  frequency: string;
  duration: string;
  flakeScore: string;
} => {
  if (!isLightTheme(theme)) {
    return {
      passRate: darkModeStatusMetricAccentColors.green,
      frequency: darkModeStatusMetricAccentColors.pink,
      duration: darkModeStatusMetricAccentColors.purple,
      flakeScore: darkModeStatusMetricAccentColors.orange,
    };
  }

  return {
    passRate: lightModeChartStatusColors.green,
    frequency: lightModeChartStatusColors.pink,
    duration: lightModeChartStatusColors.purple,
    flakeScore: lightModeStatusMetricAccentColors.orange,
  };
};

export const resolveMiniMetricTooltipColors = (
  theme: Theme,
): {
  passRate: string;
  frequency: string;
  duration: string;
  flakeScore: string;
} => {
  if (!isLightTheme(theme)) {
    return {
      passRate: darken(uiTokens.statusColors.green, chartStatusDarkenCoefficient),
      frequency: darken(uiTokens.statusColors.pink, chartStatusDarkenCoefficient),
      duration: darken(uiTokens.statusColors.purple, chartStatusDarkenCoefficient),
      flakeScore: darken(uiTokens.statusColors.orange, chartStatusDarkenCoefficient),
    };
  }

  return {
    passRate: lightModeChartStatusColors.green,
    frequency: lightModeChartStatusColors.pink,
    duration: lightModeChartStatusColors.purple,
    flakeScore: lightModeStatusMetricAccentColors.orange,
  };
};

export const resolveMetricTrendLineColors = (
  theme: Theme,
): {
  totalRuns: string;
  passRate: string;
  frequency: string;
  duration: string;
  flakeScore: string;
} => {
  if (!isLightTheme(theme)) {
    return {
      totalRuns: darkModeStatusMetricAccentColors.teal,
      passRate: darkModeStatusMetricAccentColors.green,
      frequency: darkModeStatusMetricAccentColors.pink,
      duration: darkModeStatusMetricAccentColors.purple,
      flakeScore: darkModeStatusMetricAccentColors.orange,
    };
  }

  return {
    totalRuns: lightModeChartStatusColors.teal,
    passRate: lightModeChartStatusColors.green,
    frequency: lightModeChartStatusColors.pink,
    duration: lightModeChartStatusColors.purple,
    flakeScore: lightModeStatusMetricAccentColors.orange,
  };
};

export const resolveMetricTrendTooltipColors = (
  theme: Theme,
): {
  totalRuns: string;
  passRate: string;
  frequency: string;
  duration: string;
  flakeScore: string;
} => {
  const tooltipColors = resolveMiniMetricTooltipColors(theme);
  return {
    totalRuns: isLightTheme(theme)
      ? lightModeChartStatusColors.teal
      : darken(uiTokens.statusColors.teal, chartStatusDarkenCoefficient),
    ...tooltipColors,
  };
};

export const resolveMiniMetricFallbackLineColor = (theme: Theme): string =>
  isLightTheme(theme)
    ? lightModeChartStatusColors.neutral
    : darkModeStatusMetricAccentColors.neutral;
